// Browser-side memory store. Persists typed memory records keyed by
// (user_iri, session_iri) under a single localStorage key, plus a
// realistic ranking simulator so the cockpit's Memory tab can render
// live recall without a backend round-trip.
//
// The store mirrors the wire contract of agent/foyr_agent/memory/.
// When the backend lands, the cockpit swaps storage to /api/memory/*
// without changing the consumer code.

import type { RecallQuery, RecallResult, MemoryRecord, ScoredMemory } from "./types";
import {
  MEMORY_KINDS, RECALL_WEIGHTS, RECALL_FLOOR_COSINE, RECALL_TOP_K_DEFAULT, MEMORY_SCHEMA_VERSION,
  type MemoryKindId,
} from "../../data/generated/memory-schema.generated";

import { env } from "../env";
const KEY = env.VITE_LS_MEMORY;

function uuid(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function loadAll(): MemoryRecord[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as MemoryRecord[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveAll(records: MemoryRecord[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(records)); } catch { /* ignore */ }
}

export function remember(input: {
  user_iri: string;
  session_iri: string;
  project_iri?: string;
  kind_id: MemoryKindId;
  content: string;
  valid_to?: string;
}): MemoryRecord {
  const now = new Date().toISOString();
  const rec: MemoryRecord = {
    record_id: uuid(),
    user_iri: input.user_iri,
    session_iri: input.session_iri,
    ...(input.project_iri ? { project_iri: input.project_iri } : {}),
    kind_id: input.kind_id,
    content: input.content,
    created_at: now,
    valid_from: now,
    ...(input.valid_to ? { valid_to: input.valid_to } : {}),
  };
  const all = loadAll();
  all.push(rec);
  saveAll(all);
  return rec;
}

export function listForSession(user_iri: string, session_iri: string): readonly MemoryRecord[] {
  return loadAll().filter((r) => r.user_iri === user_iri && r.session_iri === session_iri);
}

// ── Tiny semantic similarity: lower-case token overlap, ICF-weighted.
function tokens(s: string): string[] {
  return s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}
function semantic(query: string, content: string): number {
  const q = new Set(tokens(query));
  const c = new Set(tokens(content));
  if (q.size === 0 || c.size === 0) return 0;
  let hits = 0;
  for (const t of q) if (c.has(t)) hits++;
  return hits / Math.sqrt(q.size * c.size);
}

function dayDelta(a: string, b: Date): number {
  return Math.max(0, (b.getTime() - new Date(a).getTime()) / 86400000);
}

function recency(rec: MemoryRecord, asOf: Date, halfLifeDays: number): number {
  const age = dayDelta(rec.created_at, asOf);
  return Math.exp((-Math.log(2) * age) / Math.max(1, halfLifeDays));
}

function temporalScore(rec: MemoryRecord, asOf: Date): number {
  if (!rec.valid_to) return 1;
  const validToD = new Date(rec.valid_to).getTime();
  if (asOf.getTime() <= validToD) return 1;
  const past = (asOf.getTime() - validToD) / 86400000;
  return Math.max(0, 1 - past / 30);
}

export function recall(query: RecallQuery): RecallResult {
  const asOf = query.as_of ? new Date(query.as_of) : new Date();
  const records = loadAll().filter((r) =>
    r.user_iri === query.user_iri &&
    r.session_iri === query.session_iri &&
    (!query.project_iri || r.project_iri === query.project_iri),
  );
  const candidates = records
    .map((r) => ({ r, s: semantic(query.query, r.content) }))
    .filter(({ s }) => s >= RECALL_FLOOR_COSINE);

  const hits: ScoredMemory[] = candidates.map(({ r, s }) => {
    const kindDef = MEMORY_KINDS.find((k) => k.id === r.kind_id);
    const halfLife = kindDef?.decay_half_life_days ?? 90;
    const w = RECALL_WEIGHTS;
    const temporal = temporalScore(r, asOf);
    const kind_score = kindDef?.recall_weight ?? 0.5;
    const rec = recency(r, asOf, halfLife);
    const total = round3(s * w.semantic + temporal * w.temporal + kind_score * w.kind + rec * w.recency);
    return {
      record: r,
      semantic: round3(s),
      temporal: round3(temporal),
      kind_score: round3(kind_score),
      recency: round3(rec),
      total,
    };
  });

  hits.sort((a, b) => b.total - a.total);
  return {
    query,
    hits: hits.slice(0, query.top_k ?? RECALL_TOP_K_DEFAULT),
    schema_version: MEMORY_SCHEMA_VERSION,
  };
}

function round3(n: number): number { return Math.round(n * 1000) / 1000; }
